/**
 * Commercial tier and entitlement model — free vs. Carn Plus.
 *
 * Authorised by: product-concept §12 ("Product Area 7 — Commercial strategy"), which is
 * the primary authorisation for this file. Quoting it directly, because every rule below
 * is a paraphrase of one clause in it:
 *
 *   "Consumer subscription, freemium... two tiers only: free and paid."
 *   "The free tier must be genuinely usable forever — full capture, loops, capsules,
 *    interrupt button, one shared shopping list."
 *   "Paid ('Carn Plus'...) adds: unlimited AI Sorting and breakdown, walkaround mode,
 *    full compost intelligence, multiple shared lists, and wearable complications."
 *   "The free/paid line is therefore drawn at AI compute and convenience, never at core
 *    coping function."
 *   "Sensory and accessibility settings are free forever, categorically."
 *   Ethics constraints (DECIDED, non-negotiable): "no engagement-maximising dark
 *    patterns... no fake-urgency discount timers... subscription cancellation in two
 *    taps."
 *
 * FOUNDER DECISION — docs/DEVIATIONS.md M8 — SUPERSEDES §12's CRITIC RECORD, OBJECTION 1
 * RESOLUTION. §12's Critic Record originally resolved objection 1 ("a generous free tier
 * plus expensive AI features is a margin trap") with "free-tier AI is capped by volume
 * rather than removed". The founder has since decided the free tier gets NO model-backed
 * AI at all — not a volume-capped allowance. Read M8 for the full objection/resolution;
 * the short version, load-bearing for how this file is shaped:
 *
 *   The codebase already guarantees a fully-working, zero-AI path: `apps/mobile/src/
 *   screens/sorting/proposals.ts`'s `createSuggestionSource` returns `localHeuristics`
 *   unconditionally, and product-concept §7.2 objection 2 makes "Sorting can be turned
 *   fully manual" a trust requirement, while §15/§2 require the whole app to work offline
 *   with no AI at all. So the *core coping function* — getting captured things organised
 *   at all — is the local-heuristics path, and it stays free and complete. The model is a
 *   genuine enhancement bolted on top of a thing that already works without it. §12's own
 *   red line, "never at core coping function", holds; only its Critic Record's specific
 *   ANSWER to objection 1 changes, from "capped" to "absent for free".
 *
 * STRUCTURAL LAWS ENFORCED BY THIS FILE:
 *
 *   1. Exactly two tiers, for ever. `Tier` is a closed union and `ExactlyTwo` below is a
 *      compile-time proof, in the same style `sharing.ts` uses for the three-gesture
 *      ceiling. `PLUS_CAPABILITIES` now numbers exactly SIX, not five — see
 *      `'material-palettes'`'s own doc comment below for why a paid material is not the
 *      same shape of mistake as gating a sensory setting.
 *   2. A capability that is free-forever CANNOT be passed to the paid-gate function.
 *      `capabilityAvailable` only accepts a `PlusCapability`; `FreeForeverCapability` is
 *      a disjoint type, so `capabilityAvailable('capture', status)` is a compile error,
 *      not a code-review conversation. This now ALSO covers the two capabilities added
 *      after the first adversarial review found them missing
 *      (`docs/reviews/entitlements-critique.md` S1.4): `'manual-sorting'` (the
 *      local-heuristics Sorting path M8 relies on to make "no AI for free" coherent) and
 *      `'compost-review'` (the rewarm/release/rest mechanism, which product-concept §14
 *      risk 4 calls "core, not polish" and `docs/DEVIATIONS.md` M5 already rules must
 *      stay free — see that entry before touching anything compost-related).
 *   3. A stale or unknown local snapshot never downgrades a payer. `resolveTier` reads
 *      only `status.tier` — never the snapshot's age — so however long a device has been
 *      offline (the barn has no signal; brief §3, §8), a device that last confirmed
 *      "plus" keeps reporting "plus". A billing-grace subscription (card failed, retry
 *      in progress) is still, per the store's own definition, an active subscription —
 *      this module treats it as fully entitled on EVERY gate, not just `resolveTier`
 *      (fixed per the critique's S2.1, which found this asserted but untested against
 *      the gate that actually matters).
 *   4. There is exactly ONE place in this codebase that defines the Plus-only AI abuse
 *      ceiling: `PLUS_AI_ABUSE_CEILING`, below. `functions/src/lib/rate-limit.ts` must
 *      import it rather than redeclare it (the critique's S1.3 found two competing,
 *      ~37x-apart numbers when the free tier still had its own allowance; removing that
 *      allowance removes one of the two competing definitions, and this file is now the
 *      sole source of the one that remains).
 *
 * SCOPE THIS FILE DELIBERATELY DOES NOT COVER. No RevenueCat, no store receipts, no
 * purchase flow, no paywall copy, no countdown timers — those are either a platform
 * adapter that lives outside `@carn/core` (this package has "no I/O, no platform
 * imports") or are the exact dark patterns §12's ethics constraints forbid, so they have
 * no business existing even as a data shape. What lives here is the pure question "given
 * what the device currently believes, is this capability on".
 */

// ---------------------------------------------------------------------------
// Tier — exactly two, for ever
// ---------------------------------------------------------------------------

export const TIERS = ['free', 'plus'] as const;
export type Tier = (typeof TIERS)[number];

/**
 * Compile-time proof of the two-tier ceiling (mirrors `sharing.ts`'s `ExactlyThree`). A
 * third tier is a product decision that needs a docs/DEVIATIONS.md entry, not a one-line
 * array edit.
 */
type ExactlyTwo<T extends readonly unknown[]> = T['length'] extends 2 ? T : never;
const TIER_CEILING: ExactlyTwo<typeof TIERS> = TIERS;
export const TIER_COUNT: 2 = TIER_CEILING.length;

// ---------------------------------------------------------------------------
// PlusStatus — what the device currently believes, and nothing more
// ---------------------------------------------------------------------------

/**
 * The device's local snapshot of entitlement. This is a CACHE, refreshed whenever a
 * purchase, restore, renewal, or billing event is observed — never a live query. Per
 * implementation-brief's device-is-source-of-truth rule, every read of this type is a
 * local read; no capability check in this module ever waits on the network.
 *
 * `checkedAt` exists for DIAGNOSTICS ONLY (deciding when to attempt a background
 * refresh). It deliberately plays no part in `resolveTier`'s answer — see that
 * function's doc comment for why a stale snapshot must never read as a downgrade.
 *
 * `inBillingGrace` — a card failed and the store is retrying — is entitlement-NEUTRAL
 * EVERYWHERE in this file, not just in `resolveTier`. A subscription in a billing retry
 * / grace window is, per the store's own definition, still active, and product-concept
 * §12's ethics constraints plus its "trust is the moat" argument say do not cut a payer
 * off mid-retry over a card hiccup. See `capabilityAvailable`'s tests for the mutation
 * this guarantee is checked against — the critique's S2.1 found the claim asserted here
 * but untested against the gate that actually decides anything.
 */
export type PlusStatus = {
  readonly tier: Tier;
  /** Epoch ms this snapshot was last confirmed. Never read by `resolveTier`. */
  readonly checkedAt: number;
  /** Informational only (see above). Absent/false = not in a grace window. */
  readonly inBillingGrace?: boolean;
};

/**
 * The tier to act on, from whatever the device currently has cached.
 *
 * TWO DEFAULTS, DELIBERATELY ASYMMETRIC, BOTH "PREFER GENEROUS":
 *
 *   - No snapshot at all (`undefined` — the device has never confirmed an entitlement,
 *     e.g. first launch before the first check completes) resolves to `free`. This costs
 *     nobody anything structural: the free-forever capabilities (below) are never gated
 *     on tier at all, so a moment of doubt withholds only a paid feature the device
 *     cannot yet vouch for. (Persisting a confirmed snapshot across app restarts, so a
 *     payer is never actually in this branch after their first successful check, is the
 *     platform adapter's job, not this pure function's.)
 *   - A snapshot that DOES say `plus` resolves to `plus`, however old `checkedAt` is.
 *     This is the "farmer in a barn with no signal" case: a device that confirmed Plus a
 *     month ago and has had no signal since must not silently lose walkaround mode.
 *     There is no expiry window here to tune — the only thing that can ever move a
 *     cached `plus` back to `free` is a FRESHER snapshot that itself says `free` (a real
 *     cancellation observed by the device), never the mere passage of time.
 *
 * `inBillingGrace` never enters this decision (see `PlusStatus` doc).
 */
export function resolveTier(status: PlusStatus | undefined): Tier {
  return status?.tier ?? 'free';
}

/**
 * How long ago this snapshot was confirmed. DIAGNOSTIC ONLY — for a caller deciding
 * whether to attempt a background refresh, never for deciding what the user can do
 * right now. `undefined` in, `undefined` out: there is nothing to age.
 */
export function entitlementSnapshotAgeMs(
  status: PlusStatus | undefined,
  now: number,
): number | undefined {
  if (status === undefined) return undefined;
  return Math.max(0, now - status.checkedAt);
}

// ---------------------------------------------------------------------------
// Capabilities — the five Plus adds, and the nine that can never be gated
// ---------------------------------------------------------------------------

/**
 * The exactly-five things §12 itself names, plus one added since (`'material-palettes'`,
 * below), for a closed union of six. Naming follows the source clause closely so the
 * mapping back to the doc is legible at a glance rather than requiring a lookup table.
 *
 * `'unlimited-ai-sorting-and-breakdown'` is, since the founder decision (M8), a
 * STRAIGHTFORWARD boolean Plus capability — not a volume meter with a free-tier floor.
 * Free does not reach this code path at all: there is no `PlusStatus` under which a
 * `'free'`-tier caller may call a model, full stop. See `PLUS_AI_ABUSE_CEILING` below for
 * the SEPARATE, non-paywall reason a per-user ceiling still exists even though the tier
 * gate is now a hard boolean.
 *
 * `'material-palettes'` — the paid `MaterialId`s in `@carn/core`'s `materials.ts`
 * (`calch`, `glo`, `grug`, `ffion`; `drystone` and, since RULINGS R182, `llanw` stay free,
 * per `MATERIAL_TIER`). Authorised by product-concept §12 as amended by the founder decision
 * recorded in `docs/DEVIATIONS.md` M11, and it is the ONE new capability in this file that
 * GENUINELY SHIPS today (see `PLUS_CAPABILITY_REGISTRY` below) rather than sitting
 * typed-but-dark.
 *
 * WHY THIS DOES NOT VIOLATE "sensory and accessibility settings are free forever,
 * categorically" (§12, quoted at the top of this file), even though a material is
 * something the user pays for and cannot fully render without paying:
 *
 * A material changes HUE AND CHROMA ONLY. Every ramp `@carn/tokens` builds for a paid
 * material is solved to the exact WCAG relative luminance of the corresponding Drystone
 * stop, and everything downstream that carries a contrast obligation — body text, a
 * boundary, a focus ring — is then re-solved by `theme.ts` from the RATIO it owes, not
 * from the material's own colour. That is what M11 calls "identity, not accommodation":
 * a material literally cannot reach text contrast, boundary contrast, touch-target size,
 * motion, haptics, celebration, density, reading preferences, or any Intensity channel,
 * because none of those are colour decisions a material table is even consulted for. The
 * sensory controls §12 promises free forever are the dial and its seven channels, and
 * every one of them is exactly as free and exactly as capable at `calch` as at `drystone`
 * — the paid materials add a different-looking result, never a better-working one.
 *
 * The clinching fact, not an assertion: the FREE material (`drystone`) already spans the
 * entire dial by itself, end to end, including the near-monochrome level-0 reference
 * rendering this whole design system is checked against. So there is no sensory outcome
 * anywhere in Carn that is reachable ONLY by paying — which is the actual content of
 * "never at core coping function" (§12) — and what Plus buys here is a second, third,
 * fourth and fifth wardrobe for a house whose windows, doors and locks (the sensory
 * channels) are identical in every one of them.
 */
export const PLUS_CAPABILITIES = [
  'unlimited-ai-sorting-and-breakdown',
  'walkaround-mode',
  'full-compost-intelligence',
  'multiple-shared-lists',
  'wearable-complications',
  'material-palettes',
] as const;
export type PlusCapability = (typeof PLUS_CAPABILITIES)[number];

type ExactlySix<T extends readonly unknown[]> = T['length'] extends 6 ? T : never;
const PLUS_CEILING: ExactlySix<typeof PLUS_CAPABILITIES> = PLUS_CAPABILITIES;
export const PLUS_CAPABILITY_COUNT: 6 = PLUS_CEILING.length;

/**
 * The exactly-nine things on the free side of the line, enumerated rather than left as
 * an inference from "not in `PLUS_CAPABILITIES`" — an enumerated allow-list is what makes
 * the compile-time guard below possible: the shape this file exists to prevent is a
 * capability drifting onto the wrong side of the line because no one had to name it
 * either way.
 *
 * The original seven are §12's own list: "full capture, loops, capsules, interrupt
 * button, one shared shopping list" plus "sensory and accessibility settings are free
 * forever, categorically". Two more were added after the first adversarial review
 * (`docs/reviews/entitlements-critique.md`) found the type system protected exactly the
 * seven strings someone had thought to write down, and not the two capabilities closest
 * to an actual "gate the core coping function" mistake:
 *
 *   `'manual-sorting'` — the local-heuristics Sorting path (`apps/mobile/src/screens/
 *   sorting/proposals.ts`'s `localHeuristics`; product-concept §7.2 objection 2's
 *   "Sorting can be turned fully manual"). This is the capability the founder decision
 *   (M8) LEANS ON to justify "zero AI for free" as something other than a law violation
 *   — that argument only holds if this path is itself un-gateable, so it has to be named
 *   here rather than left as an inference from the AI capability being absent.
 *
 *   `'compost-review'` — the rewarm / release / rest mechanism itself (`CompostOption` /
 *   `COMPOST_OPTIONS` in `compost.ts`, plus `selectReviewBatch`, `bulkCompostOffer`,
 *   `acceptBulkCompost`, `undoBulkCompost`, and the `rewarm`/`release`/`rest` transitions
 *   in `events.ts`), AND `releaseObservation` (`compost.ts`) — the one pattern-extraction
 *   function `docs/DEVIATIONS.md` M5 already rules must stay free, because it is pure
 *   local arithmetic with no AI compute behind it. This is DELIBERATELY NOT the same
 *   thing as the `'full-compost-intelligence'` PAID capability — see that capability's
 *   own comment above, which narrows it to the phase-2 cross-loop aggregate layer §14
 *   defers, precisely so a careless reader cannot read "full compost intelligence" as
 *   covering "the Compost feature, in full" and wrap the whole review surface in the
 *   paid gate. `docs/reviews/entitlements-critique.md` S1.4 found this boundary
 *   protected by nothing but a comment; it is now protected the same structural way as
 *   the other seven.
 */
export const FREE_FOREVER_CAPABILITIES = [
  'accessibility-settings',
  'sensory-settings',
  'capture',
  'loops',
  'capsules',
  'interrupt-button',
  'one-shared-list',
  'manual-sorting',
  'compost-review',
] as const;
export type FreeForeverCapability = (typeof FREE_FOREVER_CAPABILITIES)[number];

type ExactlyNine<T extends readonly unknown[]> = T['length'] extends 9 ? T : never;
const FREE_FOREVER_CEILING: ExactlyNine<typeof FREE_FOREVER_CAPABILITIES> =
  FREE_FOREVER_CAPABILITIES;
export const FREE_FOREVER_CAPABILITY_COUNT: 9 = FREE_FOREVER_CEILING.length;

/**
 * THE GATE. Its parameter type is `PlusCapability` — not `string`, not a union of both
 * capability types — so a `FreeForeverCapability` cannot be passed to it. That is the
 * structural guarantee product-concept §12's "never at core coping function" buys: there
 * is no code path in this module through which "gate the interrupt button" or "gate
 * rewarm/release/rest" typechecks.
 *
 * Pure and total: given a tier, the five Plus capabilities are on for `plus` and off for
 * `free`. `inBillingGrace` never enters this decision (see `PlusStatus` doc, and the
 * tests that mutate this exact function to prove it).
 */
export function capabilityAvailable(
  capability: PlusCapability,
  status: PlusStatus | undefined,
): boolean {
  return resolveTier(status) === 'plus';
}

export function isFreeForeverCapability(value: string): value is FreeForeverCapability {
  return (FREE_FOREVER_CAPABILITIES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// The offer-screen registry — advertise only what ships (docs/DEVIATIONS.md M6)
// ---------------------------------------------------------------------------

/**
 * One descriptor per `PlusCapability`, closing the loop `docs/DEVIATIONS.md` M4 and M6
 * describe: the capability enum is complete against §12, but the offer screen must
 * advertise only what is user-reachable today, because "a bullet point for a feature
 * that does not exist is not a stale comment — it is the app taking money for a promise
 * it cannot keep" (M6).
 */
export type PlusCapabilityDescriptor = {
  readonly id: PlusCapability;
  /**
   * Whether the offer screen may render this capability today. `capabilityAvailable`
   * does NOT consult this field, on purpose: M6's own words are "a capability with
   * `shipping: false` is still fully typed and fully gated — the gate is live, it simply
   * has nothing to stop yet." `shipping` governs ADVERTISING, never entitlement.
   */
  readonly shipping: boolean;
};

/**
 * Today: the AI capability and `material-palettes` are the two real, user-reachable
 * bundled items, per `docs/DEVIATIONS.md` M4 (and M11 for the second):
 *
 *   - `unlimited-ai-sorting-and-breakdown` — shipping. Since M8 this was the SOLE thing
 *     money bought that a free user could not get at all; every other bundled item below
 *     is typed and gated, but had nothing yet to unlock.
 *   - `material-palettes` — shipping (M11). `calch`, `glo`, `grug` and `ffion` in
 *     `@carn/core`'s `materials.ts` are real, user-reachable, rendered choices, gated
 *     through `effectiveMaterial` exactly as this registry's `shipping: true` promises.
 *     (`llanw` moved to the free side, RULINGS R182 — see `MATERIAL_TIER`.)
 *   - `walkaround-mode` — not shipping. Exists today only as a capture *origin channel*
 *     (`'walkaround'` in `model.ts`); the narrated-stroll mode itself is phase 2 (§14).
 *   - `full-compost-intelligence` — not shipping. The phase-2 cross-loop aggregate layer
 *     (§14); see M5 and `FREE_FOREVER_CAPABILITIES`'s doc comment for why the free
 *     `releaseObservation` is NOT this capability wearing a different hat.
 *   - `multiple-shared-lists` — SHIPPING as of RULINGS R124. M4's note that "there is no
 *     user-reachable 'create a second list' control" was true when M4 was written and is not
 *     true now: `app/list/new.tsx` is that control, `ListsIndexSurface` asks
 *     `canCreateShoppingList` before offering it, and `engine.ts` asks again at the write.
 *     M4's closing sentence — "Building the multi-list UI is product scope beyond this
 *     workstream" — is discharged rather than deleted (docs/DEVIATIONS.md L5).
 *
 *     This flag was left at `false` in the commit that built all of that, while R124's build
 *     consequence and L5 both stated it had flipped. The consequence was precise and bad: a free
 *     user who hit the cap, read `plus.gate.secondSharedList` and tapped through to the offer
 *     screen arrived at a page that did not mention multiple lists, because `PlusSurface` filters
 *     on this flag. Two documents asserted a fact the code contradicted — the exact failure M4's
 *     own CORRECTION note was written about — and the entitlement test was green *because* the
 *     flip had been skipped. Found by adversarial review (docs/reviews/lists-plural-critique.md
 *     S1.1), not by the author.
 *   - `wearable-complications` — not shipping. No watch target exists in the repo (M4).
 *
 * Turning any one of these on is a one-line flip in the SAME commit that makes the
 * feature reachable; the offer screen re-renders itself with no further code change.
 */
export const PLUS_CAPABILITY_REGISTRY: readonly PlusCapabilityDescriptor[] = [
  { id: 'unlimited-ai-sorting-and-breakdown', shipping: true },
  { id: 'walkaround-mode', shipping: false },
  { id: 'full-compost-intelligence', shipping: false },
  { id: 'multiple-shared-lists', shipping: true },
  { id: 'wearable-complications', shipping: false },
  { id: 'material-palettes', shipping: true },
];

// ---------------------------------------------------------------------------
// The Plus-only AI abuse ceiling — NOT a paywall (brief §6, Critic Record objection 2)
// ---------------------------------------------------------------------------

const HOUR_MS = 60 * 60 * 1000;

/**
 * The shape a per-user rate/volume cap is checked against. Matches
 * `functions/src/lib/rate-limit.ts`'s `AiCapLimits` field-for-field ON PURPOSE: that file
 * owns the pure rolling-window EVALUATOR (`evaluateAiCap`/`recordAiUsage` — generic
 * rate-limiting mechanics with no product/tier meaning of their own, also reused there
 * for the share-link limiter, which has nothing to do with entitlement), while THIS file
 * owns the one NUMBER that is actually about the free/paid line: what a Plus account's
 * ceiling is. `functions/` imports the constant below rather than redeclaring it — see
 * LAW 4 in the header comment.
 *
 * "FIELD-FOR-FIELD ON PURPOSE" IS LOAD-BEARING, AND WAS TESTED BY A RENAME. The second field
 * was `maxCostCentsPerWindow` until the AI proxy's cap moved from cents to tokens
 * (docs/DEVIATIONS.md N7) — a change that had to land in both files in the same commit or
 * `PLUS_AI_ABUSE_CEILING` would no longer satisfy the evaluator's parameter type. It did, and
 * `tsc` is what enforces that: there is no adapter between these two declarations to absorb a
 * drift, which is exactly the property this comment is asking a future editor to preserve.
 */
export type AiCapLimits = {
  readonly maxRequestsPerWindow: number;
  readonly maxTokensPerWindow: number;
  readonly windowMs: number;
};

/**
 * THE Plus-tier request/token ceiling — an ABUSE CEILING, NOT A PRODUCT LIMIT. Read this
 * comment before touching this constant; the two likeliest misreadings are opposite and
 * both wrong.
 *
 * WHAT IT IS NOT. Since the founder decision (M8), Plus is the ONLY tier that may call a
 * model at all — `capabilityAvailable('unlimited-ai-sorting-and-breakdown', status)` is
 * the single, hard, boolean gate for that, and a `free` caller is refused before this
 * constant is ever consulted. That makes it tempting to misread this number as "the Plus
 * quota" or "the paid plan's real limit" — IT IS NEITHER. §12 promises Plus "unlimited AI
 * Sorting and breakdown" as a literal claim (see `capabilityAvailable`'s doc comment: the
 * gate is a plain boolean with no metering behind it). A future reader who "cleans this
 * up" by lowering this number to shape Plus usage, or who deletes it believing
 * "unlimited" means no cap of any kind applies anywhere, has broken this constant's one
 * job either way.
 *
 * WHAT IT IS. implementation-brief §6, Critic Record objection 2 (upheld): "Account-level
 * gateway rate limits could let one abusive user starve everyone during a spike... per-
 * user caps in our Function layer are therefore load-bearing, not decorative — they must
 * reject before the gateway limit is approached." That protects the shared Netlify AI
 * Gateway account, not "whoever hasn't paid" — a single compromised or scripted Plus
 * credential is exactly as capable of starving the account-wide limit as any other
 * caller, and now that Plus is the ONLY caller of the model, this ceiling is the SOLE
 * thing standing between one runaway Plus credential and the account-wide gateway limit.
 * So a Plus caller still passes through the Function layer's rate-limit evaluator, just
 * against a ceiling sized so no plausible legitimate usage pattern (heavy daily Sorting
 * plus walkaround plus breakdown sessions, every day) ever brushes it, while a runaway
 * loop or a leaked/shared credential still gets caught before it can move the
 * account-wide needle.
 *
 * PREDICTABILITY (§12's ethics constraints, raised by `docs/reviews/entitlements-
 * critique.md` S2.2 against the now-deleted free-tier allowance): DOES NOT APPLY HERE.
 * §12's predictability concern is about a number the USER experiences and must be able
 * to reason about; this ceiling is never rendered, never mentioned in any copy, and an
 * honest Plus user should never brush it in the lifetime of their subscription. A
 * rolling window with no user-visible reset date is the right shape for a number nobody
 * ever sees — which is exactly the case the earlier free-tier allowance was NOT. That
 * predictability question is now moot rather than answered, because the thing it was
 * about (a user-facing cap) no longer exists. Do not re-litigate it here.
 *
 * THE NUMBERS. A placeholder, exactly like `functions/src/lib/rate-limit.ts`'s own
 * disclaimer on `DEFAULT_FREE_TIER_LIMITS` — not a figure derived from real per-model
 * cost or usage telemetry, and the phase-1 unit-economics workstream (§12's Critic
 * Record, objection 1) should re-derive it once real Plus-usage and gateway-cost data
 * exist. Sized as a flat 20x multiple of a free-side baseline that predates this file
 * (50 requests / 200,000 tokens per rolling 24h) and is kept only as the multiplier's basis,
 * never as a live free-tier cap, since free no longer reaches the gateway at all: 1,000
 * requests and 4,000,000 tokens per rolling 24 hours. That is one order of magnitude beyond
 * what a very heavy single-day user could plausibly generate by hand — the headroom an abuse
 * ceiling needs, and a product quota never should have.
 *
 * THE SECOND FIGURE IS TOKENS AND USED TO BE CENTS ($40.00 = `4000`). Converting it is the one
 * place in that change (docs/DEVIATIONS.md N7) where a mechanical rename would have been a
 * serious bug rather than a cosmetic one, so the arithmetic is written out. A single call costs
 * roughly `ai-gateway.ts`'s `DEFAULT_MAX_OUTPUT_TOKENS` (512) plus its prompt — call it ~4,000
 * tokens with generous room. 1,000 requests x 4,000 = 4,000,000, which keeps this constant's
 * defining property: the token ceiling and the request ceiling are reached at *approximately
 * the same time*, so neither dimension silently becomes the real limit.
 *
 * CARRYING `4000` OVER UNCHANGED WOULD HAVE BEEN CATASTROPHIC, and is worth naming so nobody
 * "simplifies" it back. 4,000 tokens is about four requests. A Plus subscriber promised
 * "unlimited AI Sorting and breakdown" (§12) would have been cut off mid-morning, every day, by
 * a constant whose own doc comment forbids exactly that — and the failure would have surfaced as
 * a `429` the copy layer reports as a cap reached, i.e. as *working as designed*. An abuse
 * ceiling that a legitimate user reaches is not a tight abuse ceiling; it is a product limit
 * that nobody decided to ship.
 */
export const PLUS_AI_ABUSE_CEILING: AiCapLimits = {
  maxRequestsPerWindow: 1000,
  maxTokensPerWindow: 4_000_000, // 1,000 requests x ~4,000 tokens
  windowMs: 24 * HOUR_MS,
};
